import React from "react";
import useTheme from "../../../../common/hooks/useTheme";
import { Theme } from "../../../../providers/ThemeProvider";
import HealthScoreBar from "../../../consultation/components/section2-systems/HealthScoreBar";
import { useScoreSidePanel } from "./ScoreSidePanelContext";

/**
 * Score-screen header card for a selected system in `SystemDetails`.
 *
 * Layout:
 *   ┌────────────────────────────────────────────┐
 *   │ System name                     100%       │ ← name + score
 *   │ ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ │ ← HealthScoreBar
 *   │  Sev. Lim.   Lim.   Neutral  Good   Exc.  │   (5-segment + tier labels)
 *   └────────────────────────────────────────────┘
 *
 * Reuses `HealthScoreBar` from the consultation (Section 2 system detail
 * panel) so the same visual encoding shows up in both surfaces. The bar's
 * embedded tier labels supersede the standalone "category" caption that
 * the old gauge-based layout had above it.
 */
type Props = {
  name: string;
  score: number;
  scoreCategory?: number;
  onClick?: () => void;
};

const SystemScoreHeader = ({ name, score, scoreCategory, onClick }: Props) => {
  const theme: Theme = useTheme();
  const { isOpen, contentType } = useScoreSidePanel();
  const isSelected = isOpen && contentType === "system";

  return (
    <div
      style={styles.card(theme, isSelected)}
      onClick={onClick}
    >
      <div style={styles.titleRow}>
        <span style={styles.name(theme)}>{name}</span>
        <span style={styles.score(theme)}>{score}%</span>
      </div>
      <HealthScoreBar score={score} scoreCategory={scoreCategory ?? 0} />
    </div>
  );
};

export default SystemScoreHeader;

const styles = {
  card: (theme: Theme, isSelected: boolean) => ({
    display: "flex",
    flexDirection: "column" as const,
    gap: "8px",
    padding: "12px",
    borderRadius: "8px",
    backgroundColor: theme.colors_ds.NEUTRAL_BG_MAIN,
    border: `1px solid ${
      isSelected
        ? theme.colors_ds.PRIMARY_ICON_MAIN
        : theme.colors_ds.NEUTRAL_BORDER_DEFAULT
    }`,
    boxShadow: isSelected
      ? "0px 8px 20px 0px rgba(0, 0, 0, 0.06), 0px 2px 8px 0px rgba(0, 0, 0, 0.04)"
      : "none",
    cursor: "pointer",
  }),
  titleRow: {
    display: "flex",
    alignItems: "baseline",
    justifyContent: "space-between",
    gap: "8px",
  },
  name: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "16px",
    lineHeight: 1.4,
    letterSpacing: "-0.36px",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
  }),
  score: (theme: Theme) => ({
    fontFamily: "var(--font-Bold)",
    fontSize: "16px",
    lineHeight: 1.4,
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
  }),
};
